import { emptyInList, getTextWidth } from '../../../helpers/common'
import { getFontSize } from '../../../helpers/fontSize'
import { fixDivide, fixMultiply } from '../../../helpers/math'
import { formatByTheme } from '../../../helpers/theme'
import { formatVal } from '../../../helpers/value'
import { TextEllipsisStaticLine } from '../../textEllipsis/TextEllipsisStaticLine'
import {
  AXIS_LABEL_POSITION,
  HEIGHT_XAXIS,
  TICK_MARGIN_XAXIS,
  TICK_MARGIN_YAXIS,
} from './constants'

export const getYAxisProps = ({ data, yAxis, typeFontSize, locale }) => {
  return yAxis.map((config) => {
    const props = {
      orientation: 'left',
      type: 'number',
      tickCount: 7,
      tick: { fill: formatByTheme('#9C9C9C') },
      tickStyle: {},
      lineClamp: 1,
      axisLine: false,
      tickSize: 0,
      tickMargin: TICK_MARGIN_YAXIS,
      ...config,
    }

    if (props.labelText) {
      if (!props.labelPosition) {
        props.labelPosition =
          props.orientation === 'left'
            ? AXIS_LABEL_POSITION.LEFT
            : AXIS_LABEL_POSITION.RIGHT
      }

      props.label = getLabelOptionYAxis({
        labelText: props.labelText,
        labelPosition: props.labelPosition,
      })
    }

    if (props.type === 'category') {
      return getYAxisCategoryProps(data, props, typeFontSize)
    }

    return getYAxisNumberProps(data, props, typeFontSize, locale)
  })
}

const getYAxisCategoryProps = (data, props, typeFontSize) => {
  let { dataKey, tickFormatter, labelText, labelPosition, width, ...others } =
    props

  if (width === undefined) {
    width = getYAxisWidth({
      ticks: data.map((item) => item[dataKey]),
      tickFormatter,
      labelText,
      labelPosition,
      fontSize: getFontSize(props.tick?.fontSize, typeFontSize),
    })
  }

  const tick = <CustomizedYAxisTick data={data} yAxisProps={props} />

  return {
    dataKey,
    tickFormatter,
    width,
    ...others,
    tick,
  }
}

const CustomizedYAxisTick = (props) => {
  const { x, y, width, height, tickFormatter, payload, data, yAxisProps } =
    props

  const tickHeight = height / data.length - 4 // 4 is 4px gap between ticks

  const style =
    typeof yAxisProps.tickStyle === 'function'
      ? yAxisProps.tickStyle(payload.value)
      : yAxisProps.tickStyle

  return (
    <g>
      <foreignObject
        x={x - width}
        y={y - tickHeight / 2}
        height={tickHeight}
        width={width}
        color={formatByTheme('#9C9C9C')}
      >
        <div
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            textAlign: 'end',
            ...(style || {}),
          }}
        >
          <TextEllipsisStaticLine
            text={tickFormatter ? tickFormatter(payload.value) : payload.value}
            isI18n={false}
            lineClamp={yAxisProps.lineClamp}
            appendStyle={{
              paddingRight:
                yAxisProps.lineClamp && yAxisProps.lineClamp > 1 ? 8 : 0,
            }}
          />
        </div>
      </foreignObject>
    </g>
  )
}

export const getYAxisNumberProps = (data, props, typeFontSize, locale) => {
  let {
    tickFormatter,
    labelText,
    labelPosition,
    width,
    ticks,
    decimalLength,
    ...others
  } = props

  if (!ticks) {
    ticks = getNiceTicks(data, props)
  }

  const countFormat = !isInValidValue(decimalLength) ? decimalLength : 2

  if (!tickFormatter) {
    tickFormatter = (val) => formatVal(val, locale, undefined, countFormat)
  }

  if (width === undefined) {
    width = getYAxisWidth({
      ticks,
      tickFormatter,
      labelText,
      labelPosition,
      unit: props.unit,
      fontSize: getFontSize(props.tick?.fontSize, typeFontSize),
    })
  }

  return {
    width,
    ticks,
    domain: [(ticks[0], ticks[ticks.length - 1])],
    decimalLength: countFormat,
    tickFormatter,
    ...others,
  }
}

export const getXAxisProps = ({
  data,
  xAxis,
  locale,
  typeFontSize,
  chartWidth,
  margin,
  yAxisProps,
}) => {
  return xAxis.map((config) => {
    const props = {
      xAxisId: 0,
      type: 'category',
      tickCount: 5,
      axisLine: { stroke: formatByTheme('#D5D5D5') },
      tickStyle: {},
      lineClamp: 1,
      tickSize: 0,
      interval: config.type === 'number' ? 'preserveStartEnd' : 0,
      tickMargin: TICK_MARGIN_XAXIS,
      height: HEIGHT_XAXIS,
      ...config,
    }

    const fontSize = getFontSize(props.tick?.fontSize, typeFontSize)

    if (props.labelText) {
      props.label = getLabelOptionXAxis({
        labelText: props.labelText,
      })
      props.height += 16
    }

    if (props.type === 'number') {
      return getXAxisNumberProps(
        data,
        props,
        locale,
        fontSize,
        chartWidth,
        margin,
        yAxisProps,
      )
    }

    return getXAxisCategoryProps(data, props)
  })
}

const getXAxisNumberProps = (
  data,
  props,
  locale,
  fontSize,
  chartWidth,
  margin,
  yAxisProps,
) => {
  let { tickFormatter, decimalLength, ...others } = props

  const countFormat = !isInValidValue(decimalLength) ? decimalLength : 2

  if (!tickFormatter) {
    tickFormatter = (val) => formatVal(val, locale, undefined, countFormat)
  }

  const width =
    chartWidth - margin.left - margin.right - yAxisProps.map((y) => y.width)

  const ticks = getNiceTicks(
    data,
    { ...props, tickFormatter },
    { width, fontSize },
  )

  return {
    ticks,
    domain: [(ticks[0], ticks[ticks.length - 1])],
    decimalLength: countFormat,
    tickFormatter,
    ...others,
  }
}

const getXAxisCategoryProps = (data, props) => {
  let { tick, ...others } = props

  if (!tick) {
    tick = <CustomizedXAxisTick data={data} xAxisProps={props} />
  }

  return {
    tick,
    ...others,
  }
}

const CustomizedXAxisTick = (props) => {
  const { x, y, width, height, tickFormatter, payload, data, xAxisProps } =
    props

  const tickWidth = width / data.length - 4 // 4 is 4px gap between ticks

  const coordinate = xAxisProps.tickPosition
    ? xAxisProps.tickPosition({ tickWidth, ...props })
    : { x: x - tickWidth / 2, y }

  return (
    <g>
      <foreignObject
        {...coordinate}
        height={height}
        width={tickWidth}
        color={formatByTheme('#9C9C9C')}
      >
        <div
          style={{
            textAlign: 'center',
            ...(xAxisProps.tickStyle || {}),
          }}
        >
          <TextEllipsisStaticLine
            text={tickFormatter ? tickFormatter(payload.value) : payload.value}
            isI18n={false}
            lineClamp={xAxisProps.lineClamp}
          />
        </div>
      </foreignObject>
    </g>
  )
}

const getNiceTicks = (data, props, { width, fontSize } = {}) => {
  let {
    dataKeys,
    isGroupBar = false,
    isStackedBar = false,
    isLineChart = false,
    isBarChart = false,
    isFloatBar = false,
    isScatterChart = false,
    isLineArea = false,
    lineKeyWithStackBar,
    tickCount = 5,
    tickFormatter,
    isOppositeTick = false,
  } = props

  let [minVal, maxVal] = yExtentsCharts(
    data,
    dataKeys,
    isStackedBar,
    isFloatBar,
    isLineChart,
    isScatterChart,
    isLineArea,
    lineKeyWithStackBar,
  )

  if (isGroupBar && isStackedBar) {
    let [yMin, yMax] = [0, 0]
    dataKeys.forEach((keyStack) => {
      const [newMin, newMax] = emptyInList(data, keyStack)
        ? [0, 0]
        : yExtentsCharts(data, keyStack, true, false)
      ;[yMin, yMax] = [Math.min(newMin, yMin), Math.max(newMax, yMax)]
    })
    ;[minVal, maxVal] = [yMin, yMax]
  }

  // As for bar chart, always there is zero value at y Axis
  if (isBarChart) {
    if (minVal > 0) minVal = 0
    if (maxVal < 0) maxVal = 0
    //  Configure ticks values symmetrically about the origin chart
    if (isOppositeTick) {
      const absMax = Math.max(Math.abs(minVal), Math.abs(maxVal))
      minVal = -absMax
      maxVal = absMax
    }
  }

  if (width) {
    tickCount = recalculateNumberTickCount({
      width,
      minVal,
      maxVal,
      oldTickCount: tickCount,
      tickFormatter,
      fontSize,
    })
  }

  const ticks = getRoundedTickValues([minVal, maxVal], tickCount)

  if (isFloatBar) {
    ticks[0] = minVal
    ticks[ticks.length - 1] = maxVal
  }

  return ticks
}

const recalculateNumberTickCount = ({
  width,
  minVal,
  maxVal,
  oldTickCount,
  tickFormatter,
  fontSize,
}) => {
  let tickCount = oldTickCount

  const textMaxWidth = Math.max(
    ...[minVal, maxVal].map((val) =>
      Math.ceil(getTextWidth(tickFormatter(val), fontSize)),
    ),
  )
  const calcTicks = Math.max(Math.floor(Math.max(0, width) / textMaxWidth), 2)
  tickCount = calcTicks <= oldTickCount ? calcTicks : oldTickCount

  return tickCount
}

const getYAxisWidth = ({
  ticks,
  tickFormatter = (val) => val,
  labelText,
  labelPosition,
  unit,
  labelFontSize = 12,
  fontSize = 12,
}) => {
  const LABEL_GAP = 16
  let yAxisWidth = 0
  if (ticks.length) {
    const tickFormat = ticks.map((tick) => tickFormatter(tick) + (unit || ''))
    for (const tick of tickFormat) {
      const width = getTextWidth(tick, fontSize)
      if (width > yAxisWidth) {
        yAxisWidth = width
      }
    }
  }

  if (labelText && labelPosition) {
    if (
      [AXIS_LABEL_POSITION.TOP_LEFT, AXIS_LABEL_POSITION.TOP_RIGHT].includes(
        labelPosition,
      )
    ) {
      const labelWidth = Math.ceil(getTextWidth(labelText, labelFontSize))
      yAxisWidth = Math.max(yAxisWidth, labelWidth)
    } else {
      yAxisWidth += labelFontSize + LABEL_GAP
    }
  }

  yAxisWidth += TICK_MARGIN_YAXIS

  return yAxisWidth
}

const yExtentsCharts = (
  data,
  arrKey,
  isStackedBar = false,
  isFloatBar = false,
  isLineChart = false,
  isScatterChart = false,
  isLineArea = false,
  lineKeyWithStackBar,
) => {
  const [min, max] = getMinMax(
    data,
    arrKey,
    isStackedBar,
    isFloatBar,
    isLineArea,
  )

  if (lineKeyWithStackBar) {
    const [minLine, maxLine] = formatMinMaxWithLine(
      ...getMinMax(data, [lineKeyWithStackBar]),
    )
    return formatMaxMin([Math.min(min, minLine), Math.max(max, maxLine)])
  }

  if (isLineChart) {
    return formatMaxMin(formatMinMaxWithLine(min, max))
  }
  if (isScatterChart) {
    return formatMaxMin(formatMinMaxScatter(min, max))
  }
  return formatMaxMin([min, max])
}

// Increase max and decrease min for line chart to make chart more beautiful
const formatMinMaxWithLine = (min, max) => {
  const newMin = min - (max - min) * 0.1
  const newMax = max + (max - min) * 0.1
  return [
    min === 0 || (min > 0 && newMin < 0) || (max > 2 * min && min > 0)
      ? 0
      : newMin,
    max === 0 || (max < 0 && newMax > 0) || (max > 2 * min && max < 0)
      ? 0
      : newMax,
  ]
}

const formatMinMaxScatter = (min, max) => {
  const newMin = min - (max - min) * 0.12
  const newMax = max + (max - min) * 0.12
  return [newMin, newMax]
}

const getRoundedTickValues = ([min, max], ticks = 6) => {
  ticks = ticks - 1

  if (min < 0 && max > 0) {
    return getYAxisHasZero([min, max], ticks)
  }

  const roundOffsetMinMax = findRoundOffset([min, max])
  const roundedMax = roundMaxValue(max, roundOffsetMinMax)
  const roundedMin = roundMinValue(min, roundOffsetMinMax)
  const offset = (roundedMax - roundedMin) / ticks
  const roundedOffset = roundOffset(offset, roundOffsetMinMax)

  const results = []
  for (let i = 0; i <= ticks; i++) {
    min >= 0
      ? results.push(roundedMin + i * roundedOffset)
      : results.unshift(roundedMax - i * roundedOffset)
  }

  return results
}

const getMinMax = (
  data,
  arrKey,
  isStackedBar = false,
  isFloatBar = false,
  isLineArea = false,
) => {
  let max = -Infinity
  let min = Infinity

  if (isStackedBar) {
    data.forEach((item) => {
      let newMax = 0
      let newMin = 0
      arrKey.forEach((key) => {
        newMax += item[key] && item[key] >= 0 ? item[key] : 0
        newMin += item[key] && item[key] <= 0 ? item[key] : 0
      })
      max = Math.max(newMax, max)
      min = Math.min(newMin, min)
    })
  } else if (isFloatBar) {
    const list = []
    const dataKey = arrKey[0]
    data.forEach((item) => {
      item[dataKey].forEach((value) => {
        if (!isInValidValue(value)) list.push(value)
      })
    })
    const newMax = list.length ? getMaxData(list) : 0
    const newMin = list.length ? getMinData(list) : 0
    max = Math.max(newMax, max)
    min = Math.min(newMin, min)
  } else {
    arrKey.forEach((key) => {
      const list = []
      data.forEach((item) => {
        if (!isInValidValue(item[key])) {
          list.push(item[key])
        }
      })
      const lineAreaData = isLineArea ? handleLineAreaData(list) : list
      const newMax = lineAreaData.length ? getMaxData(lineAreaData) : 0
      const newMin = lineAreaData.length ? getMinData(lineAreaData) : 0
      max = Math.max(newMax, max)
      min = Math.min(newMin, min)
    })
  }

  return [min, max]
}

const isInValidValue = (val) => {
  return val === undefined || val === null
}

const getMaxData = (array) => {
  return array.reduce((a, b) => Math.max(a || 0, b ? parseFloat(b) : 0))
}

const getMinData = (array) => {
  return array.reduce((a, b) => Math.min(a || 0, b ? parseFloat(b) : 0))
}

const handleLineAreaData = (list) => {
  return list.flat().filter((item) => typeof item === 'number' && !isNaN(item))
}

// Format min, max when min equal max
const formatMaxMin = ([min, max]) => {
  if (min === max && min === 0) {
    return [0, 100]
  }

  if (min === max) {
    return [0, 2 * min]
  }

  return [min, max]
}

const getYAxisHasZero = ([min, max], ticks) => {
  const roundOffsetMinMax = findRoundOffset([min, max])
  if (ticks <= 3) {
    const offset = roundOffset(Math.max(Math.abs(min), max), roundOffsetMinMax)
    return [-offset, 0, offset]
  } else {
    let numberIntervalNegative = (ticks / (Math.abs(min) + max)) * Math.abs(min)
    if (numberIntervalNegative < 1) {
      numberIntervalNegative = 1
    } else if (numberIntervalNegative > ticks - 1) {
      numberIntervalNegative = ticks - 1
    }
    const numberIntervalPositive = ticks - numberIntervalNegative
    //case 1: floor numberIntervalNegative, ceil numberIntervalPositive
    const offsetCase1 = roundOffset(
      Math.max(
        Math.floor(numberIntervalNegative)
          ? Math.abs(min) / Math.floor(numberIntervalNegative)
          : 1,
        Math.ceil(numberIntervalPositive)
          ? max / Math.ceil(numberIntervalPositive)
          : 1,
      ),
      roundOffsetMinMax,
    )
    //case 2: ceil numberIntervalNegative, floor numberIntervalPositive
    const offsetCase2 = roundOffset(
      Math.max(
        Math.ceil(numberIntervalNegative)
          ? Math.abs(min) / Math.ceil(numberIntervalNegative)
          : 1,
        Math.floor(numberIntervalPositive)
          ? max / Math.floor(numberIntervalPositive)
          : 1,
      ),
      roundOffsetMinMax,
    )

    let offset
    let roundNumberIntervalNegative
    let roundNumberIntervalPositive
    if (
      Math.max(
        offsetCase1 * Math.floor(numberIntervalNegative) - Math.abs(min),
        offsetCase1 * Math.ceil(numberIntervalPositive) - max,
      ) <=
      Math.max(
        offsetCase2 * Math.ceil(numberIntervalNegative) - Math.abs(min),
        offsetCase2 * Math.floor(numberIntervalPositive) - max,
      )
    ) {
      offset = roundOffset(offsetCase1, roundOffsetMinMax)
      roundNumberIntervalNegative = Math.floor(numberIntervalNegative)
      roundNumberIntervalPositive = Math.ceil(numberIntervalPositive)
    } else {
      offset = roundOffset(offsetCase2, roundOffsetMinMax)
      roundNumberIntervalNegative = Math.ceil(numberIntervalNegative)
      roundNumberIntervalPositive = Math.floor(numberIntervalPositive)
    }

    const negativeResults = [0]
    let negativeValue = 0
    while (negativeValue < roundNumberIntervalNegative) {
      negativeValue += 1
      negativeResults.push(negativeResults[negativeResults.length - 1] - offset)
    }

    let positiveValue = 0
    const positiveResults = [0]
    while (positiveValue < roundNumberIntervalPositive) {
      positiveValue += 1
      positiveResults.push(positiveResults[positiveResults.length - 1] + offset)
    }

    return [...new Set([...negativeResults.reverse(), ...positiveResults])]
  }
}

const roundMaxValue = (value, roundOffset) => {
  return fixMultiply(Math.ceil(fixDivide(value, roundOffset)), roundOffset)
}

const roundMinValue = (value, roundOffset) => {
  return fixMultiply(Math.floor(fixDivide(value, roundOffset)), roundOffset)
}

const findRoundOffset = ([min, max]) => {
  const absOffset = Math.abs(max - min)
  let i = 10
  let count
  if (absOffset < 1) {
    count = 1
    while (absOffset * i < 1) {
      count += 1
      i *= 10
    }
    return 1 / Math.pow(10, count + 1)
  } else {
    count = 0
    while (absOffset / i >= 1) {
      count += 1
      i *= 10
    }
    return Math.pow(10, count - 1)
  }
}

const roundOffset = (value, roundOffset) => {
  return fixMultiply(Math.ceil(fixDivide(value, roundOffset)), roundOffset)
}

const getLabelOptionYAxis = ({ labelText, labelPosition, fontSize = 12 }) => {
  const commonProps = {
    value: labelText,
    fontSize,
    fill: formatByTheme('#9C9C9C'),
  }
  switch (labelPosition) {
    case AXIS_LABEL_POSITION.LEFT:
      return {
        ...commonProps,
        position: 'insideLeft',
        offset: 8,
        angle: -90,
        style: { textAnchor: 'middle' },
      }
    case AXIS_LABEL_POSITION.RIGHT:
      return {
        ...commonProps,
        position: 'insideRight',
        offset: 8,
        angle: 90,
        style: { textAnchor: 'middle' },
      }
    case AXIS_LABEL_POSITION.TOP_RIGHT:
      return {
        ...commonProps,
        position: 'insideTopLeft',
        offset: 0,
        style: { textAnchor: 'start', transform: 'translate(8px, -36px)' },
      }
    case AXIS_LABEL_POSITION.TOP_LEFT:
      return {
        ...commonProps,
        position: 'insideTopRight',
        offset: 0,
        style: { textAnchor: 'end', transform: 'translate(8px, -36px)' },
      }
    default:
      return {}
  }
}

const getLabelOptionXAxis = ({ labelText, fontSize = 12 }) => {
  return {
    value: labelText,
    fontSize,
    fill: formatByTheme('#9C9C9C'),
    position: 'insideBottom',
    offset: 0,
    style: { textAnchor: 'middle' },
  }
}
